import React from 'react';
import { SignalResponse } from '../../../api/types';
import { AlertCircle, Calendar, CheckCircle, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StatusDropdown from './StatusDropdown';
import { getSignalGroupKey } from '../signalGrouping';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

interface SignalTableProps {
    signals: SignalResponse[];
    isLoading: boolean;
    onRowClick: (id: string) => void;
    onStatusChange: (id: string, status: 'open' | 'closed' | 'archived') => void;
    onDelete?: (id: string) => void;
    onDateChange?: (id: string, date: Date | null) => void;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    onSort?: (column: string) => void;
    showSortArrow?: boolean;
    selectedIds?: string[];
    onSelectionChange?: (ids: string[]) => void;
    showTutorialFallback?: boolean;
    isGenerating?: boolean;
}

const SignalTable: React.FC<SignalTableProps> = ({
    signals = [],
    isLoading,
    onRowClick,
    onStatusChange,
    onDelete,
    onDateChange,
    sortBy,
    sortOrder,
    onSort,
    showSortArrow = true,
    selectedIds = [],
    onSelectionChange,
    showTutorialFallback = false,
    isGenerating = false
}) => {
    const navigate = useNavigate();
    const tutorialFallbackDate = new Date().toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
    });

    const getActionLink = (signal: SignalResponse) => {
        switch (getSignalGroupKey(signal)) {
            case 'margin': return '/margin';
            case 'cash': return '/';
            case 'ap': return '/ledger?view=ap';
            case 'ar': return '/ledger?view=ar';
            case 'data': return '/ledger?status=needs_review';
            default: return '/ledger';
        }
    };
    const allSelected = signals.length > 0 && signals.every(s => selectedIds.includes(s._id || ''));
    const someSelected = signals.some(s => selectedIds.includes(s._id || ''));

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onSelectionChange) {
            if (e.target.checked) {
                onSelectionChange(signals.map(s => s._id || ''));
            } else {
                onSelectionChange([]);
            }
        }
    };

    const handleSelectRow = (id: string, checked: boolean) => {
        if (onSelectionChange) {
            if (checked) {
                onSelectionChange([...selectedIds, id]);
            } else {
                onSelectionChange(selectedIds.filter(sid => sid !== id));
            }
        }
    };

    const renderSortIndicator = (column: string) => {
        if (!showSortArrow) return null;
        if (sortBy !== column) return null;
        return (
            <span className="ml-1 inline-block text-indigo-600 font-bold">
                {sortOrder === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm text-gray-500 font-medium">Loading signals...</p>
            </div>
        );
    }

    if (!Array.isArray(signals) || signals.length === 0) {
        return (
            <div className="flex h-full min-h-[360px] flex-col bg-white">
                <div className="w-full overflow-auto">
                    <table className="w-full text-left border-collapse table-fixed min-w-[1100px]">
                        <thead className="border-b border-gray-200 bg-white text-[#4C545F] shadow-[0_1px_0_0_rgba(229,231,235,1)] [&_th]:bg-white [&_th]:font-inter [&_th]:font-medium [&_th]:text-[14px] [&_th]:leading-[120%] [&_th]:tracking-normal [&_th]:align-middle">
                            <tr className="border-b-[1px] border-gray-200 bg-white">
                                <th className="pl-6 pr-0 py-4 w-12">
                                    <input
                                        type="checkbox"
                                        disabled
                                        className="rounded border-gray-300 text-indigo-600 opacity-40"
                                    />
                                </th>
                                <th className="p-4 w-[12%]">Date</th>
                                <th className="p-4 w-[34%]">Signal</th>
                                <th className="p-4 w-[20%]">Impact</th>
                                <th className="p-4 w-[10%]">Priority</th>
                                <th className="p-4 pr-12 w-[14%]">Due On</th>
                                <th className="p-4 pr-6 w-[10%] text-right">Status</th>
                            </tr>
                        </thead>
                        {showTutorialFallback && (
                            <tbody className="bg-white divide-y divide-gray-200">
                                <tr data-tutorial="signal-first-row" className="pointer-events-none">
                                    <td className="pl-6 pr-8 py-4">
                                        <input
                                            type="checkbox"
                                            disabled
                                            className="rounded border-gray-300 text-indigo-600 opacity-40"
                                        />
                                    </td>
                                    <td className="p-4 whitespace-nowrap text-[14px] font-medium text-gray-500">
                                        {tutorialFallbackDate}
                                    </td>
                                    <td className="p-4 overflow-hidden">
                                        <div className="flex flex-col min-w-0">
                                            <span className="truncate text-[14px] text-gray-900 tracking-tight">
                                                Example signal
                                            </span>
                                            <span className="truncate text-[12px] text-gray-500">
                                                Signals will appear here when Finz finds an action to review.
                                            </span>
                                        </div>
                                    </td>
                                    <td className="p-4 overflow-hidden truncate">
                                        <span className="text-[14px] text-gray-900">$0.00</span>
                                    </td>
                                    <td className="p-4 whitespace-nowrap">
                                        <span className="rounded bg-[#FFFBCD] px-3 py-1 text-[12px] tracking-tight text-[#8C7720]">
                                            Medium
                                        </span>
                                    </td>
                                    <td className="p-4 pr-12 whitespace-nowrap">
                                        <Calendar size={16} className="text-gray-300" />
                                    </td>
                                    <td className="p-4 pr-6">
                                        <div className="flex justify-end">
                                            <span data-tutorial="signal-status-button" className="rounded border border-gray-200 px-3 py-1 text-xs text-gray-500">
                                                Open
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        )}
                    </table>
                </div>
                <div className="flex flex-1 flex-col items-center justify-center text-gray-400">
                    {isGenerating ? (
                        <>
                            <div className="mb-3 h-8 w-8 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
                            <p className="text-base font-medium text-gray-600">Generating signals...</p>
                            <p className="px-4 text-center text-sm">Fresh results will appear here when they are ready.</p>
                        </>
                    ) : (
                        <>
                            <AlertCircle size={40} className="mb-3 opacity-20" />
                            <p className="text-base font-medium">No signals found for this period.</p>
                            <p className="px-4 text-center text-sm">Try adjusting your filters or checking a different time range.</p>
                        </>
                    )}
                </div>
            </div>
        );
    }

    const getPriorityBadge = (priority: number) => {
        switch (priority) {
            case 1:
                return (
                    <span className="px-3 py-1 bg-[#FDECEC] text-[12px] text-[#D93C3F] rounded tracking-tight">
                        High
                    </span>
                );
            case 2:
                return (
                    <span className="px-3 py-1 bg-[#FFFBCD] text-[12px] text-[#8C7720] rounded tracking-tight">
                        Medium
                    </span>
                );
            default:
                return (
                    <span className="px-3 py-1 bg-[#F1F2F3] text-[12px] text-[#374151] rounded tracking-tight">
                        Low
                    </span>
                );
        }
    };

    const getImpactStyle = (impact: string) => {
        const lower = (impact || '').toLowerCase();
        if (lower.includes('+') || lower.includes('increase') || lower.includes('up') || lower.includes('growth') || lower.includes('improve')) {
            return "text-emerald-600";
        }
        if (lower.includes('-') || lower.includes('decrease') || lower.includes('down') || lower.includes('risk') || lower.includes('decline')) {
            return "text-rose-600";
        }
        return "text-gray-900";
    };

    const getTypeColor = (type: string) => {
        switch (type.toLowerCase()) {
            case 'margin':
                return 'bg-indigo-50 text-indigo-700 border-indigo-100';
            case 'cashflow':
                return 'bg-emerald-50 text-emerald-700 border-emerald-100';
            case 'bill':
                return 'bg-amber-50 text-amber-700 border-amber-100';
            case 'invoice':
                return 'bg-blue-50 text-blue-700 border-blue-100';
            default:
                return 'bg-gray-50 text-gray-700 border-gray-100';
        }
    };

    const formatStatusBasis = (value?: string) => {
        if (!value) return null;
        if (value === 'operating') return 'Operating basis';
        if (value === 'qbo') return 'QBO basis';
        return value;
    };

    const currentPage = 1;
    const totalPages = 1;

    return (
        <div className="w-full h-full border-collapse rounded-lg bg-white flex flex-col">
            <div className="w-full border-collapse rounded-lg flex-1 min-h-0 overflow-auto">
                <table className="w-full text-left border-collapse table-fixed min-w-[1100px]">
                    <thead className="sticky top-0 z-30 border-b border-gray-200 bg-white text-[#4C545F] shadow-[0_1px_0_0_rgba(229,231,235,1)] [&_th]:bg-white [&_th]:font-inter [&_th]:font-medium [&_th]:text-[14px] [&_th]:leading-[120%] [&_th]:tracking-normal [&_th]:align-middle">
                        <tr className="border-b-[1px] border-gray-200 bg-white">
                            {/* Adjusted Checkbox Header: Added more left padding (pl-6) and right spacing (pr-8) */}
                            <th className="pl-6 pr-0 py-4 w-12">
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                    checked={allSelected}
                                    ref={input => {
                                        if (input) input.indeterminate = someSelected && !allSelected;
                                    }}
                                    onChange={handleSelectAll}
                                />
                            </th>
                            <th className="p-4 w-[12%] cursor-pointer hover:bg-gray-50 select-none" onClick={() => onSort?.('date')}>
                                Date {renderSortIndicator('date')}
                            </th>
                            <th className="p-4 w-[34%] cursor-pointer hover:bg-gray-50 select-none group" onClick={() => onSort?.('title')}>
                                Signal {renderSortIndicator('title')}
                            </th>
                            <th className="p-4 w-[20%] cursor-pointer hover:bg-gray-50 select-none" onClick={() => onSort?.('impact')}>
                                Impact {renderSortIndicator('impact')}
                            </th>
                            <th className="p-4 w-[10%] cursor-pointer hover:bg-gray-50 select-none" onClick={() => onSort?.('priority')}>
                                Priority {renderSortIndicator('priority')}
                            </th>
                            <th className="p-4 pr-12 w-[14%] cursor-pointer hover:bg-gray-50 select-none" onClick={() => onSort?.('due_on')}>
                                Due On {renderSortIndicator('due_on')}
                            </th>
                            <th className="p-4 pr-6 w-[10%] text-right">Status</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {signals.map((signal, index) => (
                            <tr
                                key={signal._id}
                                data-tutorial={index === 0 ? 'signal-first-row' : undefined}
                                onClick={() => onRowClick(signal._id || '')}
                                className="hover:bg-gray-50 cursor-pointer transition-colors group"
                            >
                                {/* Adjusted Checkbox Cell: Matching header padding */}
                                <td className="pl-6 pr-8 py-4" onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                        checked={selectedIds.includes(signal._id || '')}
                                        onChange={(e) => handleSelectRow(signal._id || '', e.target.checked)}
                                    />
                                </td>
                                <td className="p-4 whitespace-nowrap text-[14px] font-medium text-gray-500">
                                    {signal.created_at ? new Date(signal.created_at).toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '---'}
                                </td>
                                <td className="p-4 overflow-hidden">
                                    <div className="flex flex-col min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[14px] text-gray-900 group-hover:text-indigo-600 transition-colors tracking-tight truncate">
                                                {signal.title}
                                            </span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(signal.action_link || getActionLink(signal));
                                                }}
                                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-indigo-50 rounded transition-all"
                                                title="View Source"
                                            >
                                                <ExternalLink size={12} className="text-indigo-500" />
                                            </button>
                                        </div>
                                        <span className="text-[12px] text-gray-500 truncate">{signal.details}</span>
                                        {(signal.metadata?.status_basis || signal.metadata?.suppression_notes?.length) ? (
                                            <span className="mt-1 text-[11px] text-gray-400 truncate">
                                                {formatStatusBasis(signal.metadata?.status_basis)}
                                                {signal.metadata?.status_basis && signal.metadata?.suppression_notes?.length ? ' · ' : ''}
                                                {signal.metadata?.suppression_notes?.[0] ? 'Primary issue shown; secondary issue hidden.' : ''}
                                            </span>
                                        ) : null}
                                    </div>
                                </td>
                                <td className="p-4 overflow-hidden truncate">
                                    <span className={`text-[14px] ${getImpactStyle(signal.impact || '')}`}>
                                        {signal.impact}
                                    </span>
                                </td>
                                <td className="p-4 whitespace-nowrap">
                                    {getPriorityBadge(signal.priority)}
                                </td>
                                <td className="p-4 pr-12 whitespace-nowrap">
                                    <div onClick={(e) => e.stopPropagation()} className="relative">
                                        <DatePicker
                                            selected={signal.due_on ? new Date(signal.due_on) : null}
                                            onChange={(date: Date | null) => onDateChange?.(signal._id || '', date)}
                                            customInput={
                                                (() => {
                                                    if (!signal.due_on) {
                                                        return (
                                                            <button
                                                                className="p-1.5 hover:bg-gray-200 rounded-md text-gray-400 hover:text-indigo-600 transition-colors"
                                                                title="Set Due Date"
                                                            >
                                                                <Calendar size={16} />
                                                            </button>
                                                        );
                                                    }

                                                    const dueDate = new Date(signal.due_on);
                                                    const today = new Date();
                                                    today.setHours(0, 0, 0, 0);
                                                    const compareDate = new Date(dueDate);
                                                    compareDate.setHours(0, 0, 0, 0);

                                                    const diffTime = today.getTime() - compareDate.getTime();
                                                    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

                                                    if (diffDays > 0) {
                                                        return (
                                                            <div className="inline-flex items-center px-2 bg-[#FFEAEE] text-[#B5213E] rounded text-[12px] font-bold tracking-tight cursor-pointer hover:bg-amber-100 transition-colors">
                                                                {diffDays}d ago
                                                            </div>
                                                        );
                                                    }

                                                    if (diffDays > -5) {
                                                        return (
                                                            <div className="inline-flex items-center px-2 bg-[#FFFBCD] text-[#736B15] rounded text-[12px] font-bold tracking-tight cursor-pointer hover:bg-amber-100 transition-colors">
                                                                In {-diffDays}d
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div className="flex items-center gap-2 px-2 bg-gray-200 text-gray-500 rounded text-[12px] font-medium text-gray-500 cursor-pointer hover:text-indigo-600 transition-colors">
                                                            <span>{dueDate.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                                                        </div>
                                                    );
                                                })()
                                            }
                                            popperPlacement="bottom-end"
                                        />
                                    </div>
                                </td>
                                {/* Status Cell: Changed justify-end to justify-start */}
                                <td className="p-4 pr-6" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex justify-end">
                                        <div data-tutorial={index === 0 ? 'signal-status-button' : undefined}>
                                            <StatusDropdown
                                                currentStatus={signal.resolved}
                                                onStatusChange={(status) => onStatusChange(signal._id || '', status)}
                                                onDelete={onDelete ? () => onDelete(signal._id || '') : undefined}
                                            />
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="sticky bottom-0 z-10 mt-auto flex items-center justify-center gap-2 p-3 border-t border-gray-200 bg-white">
                <button
                    disabled
                    className="w-8 h-6 rounded-md bg-[#F9FAFB] text-gray-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <ChevronLeft size={16} />
                </button>

                <span className="text-sm text-gray-600">
                    Page <span className="underline underline-offset-2">{currentPage}</span> of {totalPages}
                </span>

                <button
                    disabled
                    className="w-8 h-6 rounded-md bg-[#F0F0FF] text-gray-600 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default SignalTable;
